// Dashboard — the business at a glance, all real numbers.
// Net sales (returns deducted) vs expenses, the money trend, the inquiry
// pipeline, CAP vs OSM split, stock-car investment recovery and OSM vendor
// balances — everything the backend now aggregates in /admin/dashboard/.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Card, PageHeader } from '../components/common/index.js';
import {
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiArrowRight,
  FiPackage,
  FiClipboard,
  FiBriefcase,
  FiRefreshCw,
  FiTruck,
  FiShoppingBag,
  FiCreditCard,
  FiRotateCcw,
  FiPieChart,
  FiActivity,
  FiLayers,
  FiDollarSign,
  FiRepeat,
  FiSun,
} from 'react-icons/fi';
import { analyticsApi } from '../api/analyticsApi';
import { ROUTES } from '../constants/app.constants';

// ---------- formatting ----------

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '₹0';
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 10000000) return `${sign}₹${(a / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `${sign}₹${(a / 100000).toFixed(2)} L`;
  return `${sign}₹${a.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtCompact = (n) => {
  const a = Math.abs(n);
  if (a >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (a >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (a >= 1000) return `${(n / 1000).toFixed(0)}k`;
  return `${n}`;
};

const shortDate = (value) => String(value || '').split(' at ')[0];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// ---------- small building blocks ----------

const KpiCard = ({ icon: Icon, label, value, sub, tone, delay = 0 }) => {
  const tones = {
    violet: 'bg-violet-100 text-violet-600',
    rose: 'bg-rose-100 text-rose-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    sky: 'bg-sky-100 text-sky-600',
  };
  return (
    <div
      className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-all duration-300 animate-fade-in-up"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone] || tones.violet}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 text-right">{label}</span>
      </div>
      <p className="text-2xl font-black text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
};

const MoneyRow = ({ label, value, tone = 'slate', bold = false }) => {
  const colors = {
    slate: 'text-slate-500',
    rose: 'text-rose-500',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    violet: 'text-violet-600',
  };
  return (
    <div className="flex items-center justify-between text-xs py-1">
      <span className={`${colors[tone]} font-medium`}>{label}</span>
      <span className={`${colors[tone]} ${bold ? 'font-bold' : 'font-semibold'} tabular-nums`}>{value}</span>
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-lg text-xs backdrop-blur-sm">
      <p className="font-bold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2" style={{ color: p.stroke || p.fill }}>
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.stroke || p.fill }} />
          {p.name}: <span className="font-semibold tabular-nums">{fmtMoney(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const STAGE_META = [
  { key: 'pending', label: 'Pending', hint: 'waiting for quote', bar: 'bg-amber-400' },
  { key: 'live', label: 'Live', hint: 'quote in hand', bar: 'bg-sky-500' },
  { key: 'waiting', label: 'Waiting stock', hint: 'restock needed', bar: 'bg-violet-400' },
  { key: 'hold', label: 'On Hold', hint: 'awaiting payment confirm', bar: 'bg-orange-400' },
  { key: 'won', label: 'Sold', hint: 'payment confirmed', bar: 'bg-emerald-500' },
  { key: 'loss', label: 'Lost', hint: 'no deal', bar: 'bg-rose-400' },
];

const PERIODS = [
  { key: 'today', label: 'Today', border: 'border-violet-500' },
  { key: 'this_month', label: 'This Month', border: 'border-blue-500' },
  { key: 'this_year', label: 'This Year', border: 'border-emerald-500' },
  { key: 'overall', label: 'Overall', border: 'border-amber-500' },
];

// ---------- page ----------

const DashboardPage = () => {
  const { user } = useSelector((state) => state.auth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await analyticsApi.getDashboard();
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load the dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const timeGreet = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const summary = data?.sales_summary;
  const overview = data?.overview;
  const recent = data?.recent_sales ?? [];
  const overall = summary?.overall;
  const series = data?.monthly_series ?? [];
  const pipeline = data?.pipeline;
  const capOsm = data?.cap_osm;
  const finance = data?.finance;
  const investment = data?.finance?.investment;
  const osm = data?.finance?.osm;
  const money = data?.finance?.money; // chit / money owed — may be absent on older responses
  const cars = data?.finance?.cars;   // the whole-car business — absent on older responses
  const team = data?.salespersons;    // salesperson turnover leaderboard — may be absent on older responses
  const teamMax = team?.leaderboard?.length ? Number(team.leaderboard[0].turnover) : 0;

  const overallBalance = num(overall?.balance_value);
  const donutData = capOsm ? [
    { name: 'CAP stock', value: num(capOsm.overall?.cap?.value), count: capOsm.overall?.cap?.count ?? 0, color: '#7c3aed' },
    { name: 'OSM market', value: num(capOsm.overall?.osm?.value), count: capOsm.overall?.osm?.count ?? 0, color: '#f59e0b' },
  ] : [];
  const donutTotal = donutData.reduce((a, d) => a + d.value, 0);
  const investPct = Math.max(0, Math.min(100, num(investment?.recovered_pct)));
  const moneyPct = Math.max(0, Math.min(100, num(money?.paid_pct)));

  return (
    <MainLayout>
      <div className="space-y-5 sm:space-y-8">

        {/* Page header — this screen is how the business DID; what it NEEDS
            lives on Today, so the greeting says so and links there. */}
        <PageHeader meta={`${dateStr} · ${timeGreet}, ${user?.username || 'Admin'}`}>
          <Link
            to={ROUTES.TODAY}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-600 transition-all"
          >
            <FiSun className="h-4 w-4" /> What needs me
          </Link>
          <button
            onClick={load}
            className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500 hover:border-violet-200 hover:bg-violet-50 hover:text-violet-600 transition-all"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </PageHeader>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600">{error}</div>
        )}

        {/* Business KPIs — net sales · expenses · balance · investment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          <KpiCard
            icon={FiTrendingUp} tone="violet" delay={0}
            label="Net Sales · all time"
            value={overall ? fmtMoney(overall.net_value) : '—'}
            sub={overall ? `${fmtMoney(overall.total_value)} sold − ${fmtMoney(overall.returns_value)} returned` : 'sold − returns'}
          />
          <KpiCard
            icon={FiPackage} tone="violet" delay={0.04}
            label="CAP Sales · all time"
            value={capOsm ? fmtMoney(capOsm.overall?.cap?.value) : '—'}
            sub={capOsm ? `${capOsm.overall?.cap?.count ?? 0} parts · from our own stock` : 'our own stock'}
          />
          <KpiCard
            icon={FiShoppingBag} tone="amber" delay={0.08}
            label="OSM Sales · all time"
            value={capOsm ? fmtMoney(capOsm.overall?.osm?.value) : '—'}
            sub={capOsm ? `${capOsm.overall?.osm?.count ?? 0} parts · from outside market` : 'outside market'}
          />
          <KpiCard
            icon={FiCreditCard} tone="rose" delay={0.07}
            label="Expenses · all time"
            value={overall ? fmtMoney(overall.expenses_value) : '—'}
            sub={overall ? `${overall.expenses_count} entries` : ''}
          />
          <KpiCard
            icon={FiActivity} tone={overallBalance >= 0 ? 'emerald' : 'rose'} delay={0.14}
            label="Balance"
            value={overall ? fmtMoney(overall.balance_value) : '—'}
            sub={overall
              ? `${fmtMoney(overall.net_value)} net sales − ${fmtMoney(overall.expenses_value)} expenses${overallBalance < 0 ? ' — spending exceeds sales' : ''}`
              : 'net sales − expenses'}
          />
          <KpiCard
            icon={FiLayers} tone="amber" delay={0.21}
            label="Stock Investment"
            value={investment ? fmtMoney(investment.outstanding) : '—'}
            sub={investment ? `still to recover · ${investPct}% of ${fmtMoney(investment.invested)} back` : 'to recover'}
          />
          {money && (
            <KpiCard
              icon={FiRepeat} tone="sky" delay={0.28}
              label="Chit / Money"
              value={fmtMoney(money.outstanding)}
              sub={`${moneyPct}% of ${fmtMoney(money.total)} repaid · ${money.count} open${Number(money.overdue) > 0 ? ` · ${money.overdue} overdue` : ''}`}
            />
          )}
        </div>

        {/* Money trail — the Balance, step by step, so a negative number is
            never a mystery: every figure above traces through this one line. */}
        {overall && (
          <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-sm">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Where the money went (all time)</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
              <span className="font-semibold text-slate-700">
                Sold <span className="font-black tabular-nums">{fmtMoney(overall.total_value)}</span>
                <span className="ml-1 text-[11px] font-medium text-slate-400">
                  (deals {fmtMoney(overall.sales_value)} + counter {fmtMoney(overall.counter_value)})
                </span>
              </span>
              <span className="text-slate-300">−</span>
              <span className="font-semibold text-rose-600">
                Returns <span className="font-black tabular-nums">{fmtMoney(overall.returns_value)}</span>
              </span>
              <span className="text-slate-300">=</span>
              <span className="font-semibold text-slate-700">
                Net <span className="font-black tabular-nums">{fmtMoney(overall.net_value)}</span>
              </span>
              <span className="text-slate-300">−</span>
              <span className="font-semibold text-amber-600">
                Expenses <span className="font-black tabular-nums">{fmtMoney(overall.expenses_value)}</span>
              </span>
              <span className="text-slate-300">=</span>
              <span className={`rounded-lg px-2 py-0.5 font-black tabular-nums ${overallBalance >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'}`}>
                Balance {fmtMoney(overall.balance_value)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {Number(overall.hold_value) > 0 && (
                <span className="rounded-lg bg-orange-50 px-2 py-1 text-[11px] font-semibold text-orange-700">
                  ⏳ On hold (awaiting admin confirm): {fmtMoney(overall.hold_value)} — not counted as sold yet
                </span>
              )}
              {Number(finance?.customer_refunds_due) > 0 && (
                <Link to="/payments" className="rounded-lg bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-100">
                  ↩ Still to hand back to customers: {fmtMoney(finance.customer_refunds_due)} ({finance.customer_refunds_count}) — already deducted above, cash pending →
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Period money cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {PERIODS.map((p, i) => {
            const s = summary?.[p.key];
            const bal = num(s?.balance_value);
            return (
              <div
                key={p.key}
                className={`bg-white rounded-2xl p-5 shadow-sm border border-slate-200/80 border-l-4 ${p.border} hover:shadow-md transition-all duration-300 animate-fade-in-up`}
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{p.label}</span>
                  {s && Number(s.returns_count) > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-500">
                      <FiRotateCcw className="h-2.5 w-2.5" /> {s.returns_count} return{Number(s.returns_count) === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-black text-slate-900">{s ? fmtMoney(s.net_value) : '—'}</p>
                <p className="text-slate-500 text-xs mt-0.5 mb-2.5">
                  {s ? `${s.sales_count} sale${Number(s.sales_count) === 1 ? '' : 's'}` : 'Net sales'}
                  {s && Number(s.counter_value) > 0 ? ` + ${s.counter_count} counter (${fmtMoney(s.counter_value)})` : ''}
                </p>
                <div className="border-t border-slate-100 pt-2">
                  {s && Number(s.hold_value) > 0 && (
                    <MoneyRow label={`On hold (${s.hold_count})`} value={fmtMoney(s.hold_value)} tone="amber" />
                  )}
                  {s && Number(s.returns_value) > 0 && (
                    <MoneyRow label="Returns" value={`− ${fmtMoney(s.returns_value)}`} tone="rose" />
                  )}
                  <MoneyRow label="Expenses" value={`− ${fmtMoney(s?.expenses_value ?? 0)}`} tone="amber" />
                  {/* Cars sit on their own line, never inside net sales: what a
                      buyer pays for a commission car is mostly the owner's
                      money, so folding it in would read as a record month. */}
                  {Number(s?.cars?.sold_count) > 0 && (
                    <MoneyRow
                      label={`Cars (${s.cars.sold_count})`}
                      value={`+ ${fmtMoney(s.cars.earning_value)}`}
                      tone="violet"
                    />
                  )}
                  {Number(s?.cars?.fund_returned_value) > 0 && (
                    <MoneyRow label="Back from Cars" value={`+ ${fmtMoney(s.cars.fund_returned_value)}`} tone="emerald" />
                  )}
                  <MoneyRow label="Balance" value={fmtMoney(s?.balance_value ?? 0)} tone={bal >= 0 ? 'emerald' : 'rose'} bold />
                  <MoneyRow label={`Lost (${s?.loss_count ?? 0})`} value={fmtMoney(s?.loss_value ?? 0)} tone="slate" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Trend + CAP/OSM donut */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card title="Sales vs Expenses" subtitle="Net sales (returns deducted) against expenses — last 12 months">
              <div className="h-72 -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="gSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.22} />
                        <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="net" name="Net sales" stroke="#7c3aed" strokeWidth={2.5} fill="url(#gSales)" />
                    <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#f43f5e" strokeWidth={2} fill="url(#gExp)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 flex items-center gap-5 text-xs font-semibold">
                <span className="flex items-center gap-1.5 text-violet-600"><span className="h-2.5 w-2.5 rounded-full bg-violet-600" /> Net sales</span>
                <span className="flex items-center gap-1.5 text-rose-500"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> Expenses</span>
              </div>
            </Card>
          </div>

          <Card title="CAP vs OSM" subtitle="Where sold parts came from — all time">
            {donutTotal > 0 ? (
              <>
                <div className="relative h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={82}
                        paddingAngle={3} strokeWidth={0}>
                        {donutData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-lg font-black text-slate-900">{fmtMoney(donutTotal)}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">parts sold</p>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {donutData.map((d) => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-2 font-medium text-slate-600">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} /> {d.name}
                      </span>
                      <span className="font-bold text-slate-800 tabular-nums">
                        {fmtMoney(d.value)} <span className="font-medium text-slate-400">· {d.count}</span>
                      </span>
                    </div>
                  ))}
                  {capOsm?.this_month && (
                    <p className="border-t border-slate-100 pt-2 text-[11px] text-slate-400">
                      This month: CAP {fmtMoney(capOsm.this_month.cap?.value)} · OSM {fmtMoney(capOsm.this_month.osm?.value)}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="py-12 text-center">
                <FiPieChart className="mx-auto mb-2 h-8 w-8 text-slate-200" />
                <p className="text-sm text-slate-400">No completed part sales yet</p>
              </div>
            )}
          </Card>
        </div>

        {/* Pipeline + Investment recovery + OSM payables */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card
            title="Inquiry Pipeline"
            subtitle={pipeline ? `${pipeline.total} inquiries all time` : 'all inquiries'}
            action={
              <Link to="/analytics" className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                Analytics <FiArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          >
            <div className="space-y-3.5">
              {STAGE_META.map((st) => {
                const row = pipeline?.stages?.[st.key];
                return (
                  <div key={st.key}>
                    <div className="mb-1 flex items-baseline justify-between">
                      <span className="text-xs font-semibold text-slate-600">{st.label} <span className="font-normal text-slate-400">· {st.hint}</span></span>
                      <span className="text-xs font-bold text-slate-800 tabular-nums">{row?.count ?? 0} <span className="font-medium text-slate-400">({row?.percentage ?? 0}%)</span></span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className={`h-full rounded-full ${st.bar} transition-all duration-700`} style={{ width: `${Math.min(row?.percentage ?? 0, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card
            title="Investment Recovery"
            subtitle={investment
              ? `${investment.cars} stock cars · ${investment.outside?.batches ?? 0} outside-part batches`
              : 'stock cars + outside parts'}
            action={
              <Link to="/products" className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                Stock <FiArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          >
            <div className="mb-1.5 flex items-baseline justify-between text-xs font-semibold">
              <span className="text-slate-500">Recovered {investment ? fmtMoney(investment.recovered) : '—'}</span>
              <span className="text-slate-400">of {investment ? fmtMoney(investment.invested) : '—'}</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all duration-700" style={{ width: `${investPct}%` }} />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">{investPct}% recovered · sales auto-count from dispatch</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-amber-50 px-3 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wide text-amber-600">To recover</p>
                <p className="text-base font-black text-amber-700 tabular-nums">{investment ? fmtMoney(investment.outstanding) : '—'}</p>
                <p className="text-[10px] text-amber-500">
                  {investment?.cars_recovering ?? 0} cars · {investment?.outside?.recovering ?? 0} batches recovering
                </p>
              </div>
              <div className="rounded-xl bg-emerald-50 px-3 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-600">Profit</p>
                <p className="text-base font-black text-emerald-700 tabular-nums">{investment ? fmtMoney(investment.profit) : '—'}</p>
                <p className="text-[10px] text-emerald-500">
                  {(investment?.cars_in_profit ?? 0) + (investment?.outside?.in_profit ?? 0)} past break-even
                </p>
              </div>
            </div>
            {/* Where the invested money sits — donor cars vs shelf batches */}
            {investment && (
              <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                <MoneyRow label={`Stock cars (${investment.cars})`}
                  value={`${fmtMoney(investment.cars_recovered)} of ${fmtMoney(investment.cars_invested)}`} tone="slate" />
                <MoneyRow label={`Outside parts (${investment.outside?.batches ?? 0} · ${investment.outside?.units_left ?? 0} units left)`}
                  value={`${fmtMoney(investment.outside?.recovered)} of ${fmtMoney(investment.outside?.invested)}`} tone="slate" />
              </div>
            )}
          </Card>

          <Card
            title="OSM Payables"
            subtitle="what CAP owes outside vendors"
            action={
              <Link to="/osm" className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                OSM <FiArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          >
            <div className="rounded-xl bg-rose-50 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-rose-500">Balance due to vendors</p>
              <p className="text-2xl font-black text-rose-600 tabular-nums">{osm ? fmtMoney(osm.balance_due) : '—'}</p>
              <p className="text-[11px] text-rose-400">{osm?.due_count ?? 0} purchases open{Number(osm?.to_price_count) > 0 ? ` · ${osm.to_price_count} need pricing` : ''}</p>
            </div>
            <div className="mt-3 space-y-1">
              <MoneyRow label="Advance already paid" value={fmtMoney(osm?.advance_paid ?? 0)} tone="slate" />
              <MoneyRow label="Sold to customers (OSM)" value={fmtMoney(osm?.sold_value ?? 0)} tone="emerald" bold />
            </div>
            {/* Money owed BACK to customers — over-paid advances after cancellations */}
            {Number(finance?.customer_refunds_due) > 0 && (
              <div className="mt-3 rounded-xl bg-rose-50 px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-rose-500">Refund due to customers</p>
                <p className="text-2xl font-black text-rose-600 tabular-nums">{fmtMoney(finance.customer_refunds_due)}</p>
                <p className="text-[11px] text-rose-400">
                  {finance.customer_refunds_count ?? 0} order{Number(finance.customer_refunds_count) === 1 ? '' : 's'} · cancelled after advance paid
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Cars — the whole-car business, kept in its own frame.
            Two totals, never merged: what buyers paid (mostly the owner's money
            on a commission car) and what CAP actually kept. */}
        {cars && Number(cars.stock?.total) > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card
                title="Cars"
                subtitle={`${cars.stock.total} on the books · ${cars.stock.own} ours, ${cars.stock.consignment} on commission`}
                action={
                  <Link to={ROUTES.CARS} className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                    The lot <FiArrowRight className="w-3.5 h-3.5" />
                  </Link>
                }
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-violet-50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-violet-500">On the lot</p>
                    <p className="text-2xl font-black text-violet-700 tabular-nums">
                      {(cars.stock.available ?? 0) + (cars.stock.booked ?? 0)}
                    </p>
                    <p className="text-[11px] text-violet-400">asking {fmtMoney(cars.stock.asking_value)}</p>
                  </div>
                  <div className="rounded-xl bg-amber-50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-500">Money in the lot</p>
                    <p className="text-2xl font-black text-amber-600 tabular-nums">{fmtCompact(num(cars.stock.invested))}</p>
                    <p className="text-[11px] text-amber-400">our cars + work done</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Buyers paid</p>
                    <p className="text-2xl font-black text-slate-700 tabular-nums">{fmtCompact(num(cars.gross_value))}</p>
                    <p className="text-[11px] text-slate-400">{cars.sold_count} sold — owners' share included</p>
                  </div>
                  <div className="rounded-xl bg-emerald-50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-500">CAP earned</p>
                    <p className="text-2xl font-black text-emerald-600 tabular-nums">{fmtCompact(num(cars.earning_value))}</p>
                    <p className="text-[11px] text-emerald-400">what is actually ours</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <MoneyRow label={`Sold — ours (${cars.sold_own_count})`} value={fmtMoney(cars.earning_value)} tone="emerald" />
                  <MoneyRow label={`Commission earned (${cars.sold_consignment_count})`} value={fmtMoney(cars.commission_value)} tone="violet" />
                  {Number(cars.balance_due_value) > 0 && (
                    <MoneyRow label="Still to collect from buyers" value={fmtMoney(cars.balance_due_value)} tone="amber" />
                  )}
                  {Number(cars.owner_due_value) > 0 && (
                    <MoneyRow label="Owners' money still with us" value={fmtMoney(cars.owner_due_value)} tone="rose" bold />
                  )}
                </div>
              </Card>
            </div>

            <Card title="CAP ↔ Cars" subtitle="Money lent to the car business, and what has come back">
              <div className={`rounded-xl px-4 py-3 ${num(cars.fund_outstanding) > 0 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                <p className={`text-[10px] font-bold uppercase tracking-wide ${num(cars.fund_outstanding) > 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                  Cars is holding
                </p>
                <p className={`text-2xl font-black tabular-nums ${num(cars.fund_outstanding) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {fmtMoney(cars.fund_outstanding)}
                </p>
                <p className={`text-[11px] ${num(cars.fund_outstanding) > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {num(cars.fund_outstanding) > 0 ? "of CAP's money, not back yet" : 'all square'}
                </p>
              </div>
              <div className="mt-3 space-y-1">
                <MoneyRow label="Given to Cars" value={fmtMoney(cars.fund_given)} tone="rose" />
                <MoneyRow label="Come back" value={fmtMoney(cars.fund_returned)} tone="emerald" />
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
                Money given already sits in that day's expenses; money back is added to the balance
                the day it lands. A car's profit is not counted on top — it arrives inside what
                comes back.
              </p>
            </Card>
          </div>
        )}

        {/* Chit / Money repayment — money the business owes on a schedule */}
        {money && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card
              title="Chit / Money Owed"
              subtitle={`${money.count} open · chit funds & borrows`}
              action={
                <Link to="/money-investments" className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                  Chit / Money <FiArrowRight className="w-3.5 h-3.5" />
                </Link>
              }
            >
              <div className="mb-1.5 flex items-baseline justify-between text-xs font-semibold">
                <span className="text-slate-500">Repaid {fmtMoney(money.paid)}</span>
                <span className="text-slate-400">of {fmtMoney(money.total)}</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all duration-700" style={{ width: `${moneyPct}%` }} />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">{moneyPct}% of principal repaid</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-amber-50 px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-amber-600">Outstanding</p>
                  <p className="text-base font-black text-amber-700 tabular-nums">{fmtMoney(money.outstanding)}</p>
                  <p className="text-[10px] text-amber-500">{money.active ?? 0} active</p>
                </div>
                <div className="rounded-xl bg-rose-50 px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-rose-500">Overdue</p>
                  <p className="text-base font-black text-rose-600 tabular-nums">{money.overdue ?? 0}</p>
                  <p className="text-[10px] text-rose-400">need attention</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Overview strip — real counts */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Salespersons', value: overview ? `${overview.active_salespersons}/${overview.total_salespersons}` : '—', icon: FiBriefcase, color: 'text-violet-600 bg-violet-50' },
            { label: 'Customers', value: overview?.total_customers ?? '—', icon: FiUsers, color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Inquiries', value: overview?.total_inquiries ?? '—', icon: FiClipboard, color: 'text-blue-600 bg-blue-50' },
            { label: 'Sales overall', value: overall ? Number(overall.sales_count) + Number(overall.counter_count) : '—', icon: FiShoppingBag, color: 'text-amber-600 bg-amber-50' },
          ].map((s) => {
            const I = s.icon;
            return (
              <div key={s.label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${s.color}`}>
                  <I className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-slate-900">{s.value}</p>
                  <p className="truncate text-xs text-slate-400">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Salesperson performance — turnover leaderboard */}
        {team?.leaderboard?.length > 0 && (
          <Card
            title="Salesperson Performance"
            subtitle={`${team.active}/${team.count} active · ${fmtMoney(team.total_turnover)} turnover all time`}
            action={
              <Link to="/salespersons" className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                Salespersons <FiArrowRight className="w-3.5 h-3.5" />
              </Link>
            }
          >
            <div className="space-y-3.5">
              {team.leaderboard.map((sp, i) => {
                const share = teamMax > 0 ? (Number(sp.turnover) / teamMax) * 100 : 0;
                return (
                  <div key={sp.id}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700">
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                        <span className="truncate">{sp.name}</span>
                        {!sp.is_active && <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-400">Inactive</span>}
                      </span>
                      <span className="shrink-0 text-sm font-bold text-slate-800 tabular-nums">
                        {fmtMoney(sp.turnover)} <span className="font-medium text-slate-400">· {sp.deals} {Number(sp.deals) === 1 ? 'deal' : 'deals'}</span>
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all duration-700" style={{ width: `${Math.max(share, Number(sp.deals) > 0 ? 4 : 0)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Recent sales + right column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card
              title="Recent Sales"
              subtitle="Latest dispatched orders"
              action={
                <Link to="/dispatch" className="text-sm text-violet-600 font-semibold hover:text-violet-700 flex items-center gap-1 transition-colors">
                  Dispatch <FiArrowRight className="w-3.5 h-3.5" />
                </Link>
              }
            >
              {recent.length === 0 ? (
                <div className="py-10 text-center">
                  <FiTruck className="mx-auto mb-2 h-8 w-8 text-slate-200" />
                  <p className="text-sm text-slate-400">No dispatched sales yet — they appear here once orders are dispatched</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full min-w-[480px]">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Order</th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Customer</th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Vehicle</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Amount</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3">Sold</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {recent.map((o) => (
                        <tr key={o.inquiry_id} className="hover:bg-slate-50/70 transition-colors">
                          <td className="py-3 text-sm font-semibold text-violet-600">#{o.inquiry_id}</td>
                          <td className="py-3 text-sm text-slate-700">{o.customer_name || '—'}</td>
                          <td className="py-3 text-sm text-slate-500">{o.car || o.parts || '—'}</td>
                          <td className="py-3 text-sm font-semibold text-slate-800 text-right">{o.amount ? fmtMoney(o.amount) : '—'}</td>
                          <td className="py-3 text-xs text-slate-400 text-right whitespace-nowrap">{shortDate(o.sold_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          <div className="space-y-5">
            <Card title="Today's Flow" subtitle="the daily routine, in order">
              <div className="space-y-2">
                {[
                  { label: '1 · Approve orders', to: '/orders', icon: FiShoppingBag, color: 'text-violet-600 bg-violet-50' },
                  { label: '2 · Confirm payments & balances', to: '/payments', icon: FiCreditCard, color: 'text-blue-600 bg-blue-50' },
                  { label: '3 · Dispatch orders', to: '/dispatch', icon: FiTruck, color: 'text-emerald-600 bg-emerald-50' },
                  { label: '4 · Returns & refunds', to: '/returns', icon: FiRotateCcw, color: 'text-amber-600 bg-amber-50' },
                  { label: 'OSM Purchases (vendor dues)', to: '/osm', icon: FiPackage, color: 'text-rose-600 bg-rose-50' },
                  { label: 'Customers', to: '/customers', icon: FiUsers, color: 'text-slate-600 bg-slate-100' },
                ].map((item) => {
                  const I = item.icon;
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 group transition-colors"
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.color}`}>
                        <I className="w-4 h-4" />
                      </span>
                      <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">{item.label}</span>
                      <FiArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 ml-auto transition-colors" />
                    </Link>
                  );
                })}
              </div>
            </Card>

            {/* Overall business value — real */}
            <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-5 text-white shadow-lg shadow-violet-200">
              <p className="text-violet-200 text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <FiDollarSign className="h-3.5 w-3.5" /> Net Sales Value
              </p>
              <p className="text-3xl font-black mb-1">{overall ? fmtMoney(overall.net_value) : '—'}</p>
              <p className="text-violet-200 text-sm">
                {overall ? `${Number(overall.sales_count) + Number(overall.counter_count)} sales all-time · returns deducted` : 'Across all sales'}
              </p>
              <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between text-sm">
                <span className="text-violet-200">This month</span>
                <span className="font-bold flex items-center gap-1">
                  <FiTrendingUp className="w-4 h-4" /> {summary ? fmtMoney(summary.this_month.net_value) : '—'}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-violet-200">Returned overall</span>
                <span className="font-bold flex items-center gap-1 text-amber-200">
                  <FiRotateCcw className="w-4 h-4" /> {overall ? fmtMoney(overall.returns_value) : '—'}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-violet-200">Lost overall</span>
                <span className="font-bold flex items-center gap-1 text-rose-200">
                  <FiTrendingDown className="w-4 h-4" /> {overall ? fmtMoney(overall.loss_value) : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
